/**
 * useTopicPostsGrid (Phase 2.4) — read-side projection feeding `<PostsGrid>`.
 *
 * Subscribes to `projects/{pid}/content_posts/*` filtered by `topicId` and
 * groups the result into the grid model:
 *   masters[aspectKey | '__noaspect__']   → first-column cell
 *   cells[aspectKey | '__noaspect__'][platform] → derivative cell items
 *
 * Each cell carries:
 *   - resolved cellMode (single | variants | series; falls back to 'single'
 *     when absent — pre-grid posts behave as single)
 *   - items[]: ContentPost docs sorted by creation order; for variants the
 *     `isPrimaryVariant=true` doc is hoisted to index 0; for series the
 *     `seriesIndex` ordering is honored.
 *   - state: 'empty' | 'applied' | 'stale' (lifecycle state derived from
 *     items + their staleSince marker).
 *
 * Sprint context is NOT mixed in here — that's the job of
 * `useTopicSprintAssignments` (Phase 4). The hook stays standalone so the
 * grid can render even before sprint assignments load.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { Post, PlatformId } from '../types';

export const NO_ASPECT_KEY = '__noaspect__';

export type CellMode = 'single' | 'variants' | 'series';
export type CellState = 'empty' | 'applied' | 'stale';

export interface DerivativeCellModel {
  aspectKey: string | null;
  platform: PlatformId;
  cellMode: CellMode;
  state: CellState;
  /** Items in display order: primary variant first / series in seriesIndex order. */
  items: Post[];
  /** Number of items flagged stale (master regenerated after them). */
  staleCount: number;
}

export interface MasterCellModel {
  aspectKey: string | null;
  state: CellState;
  master: Post | null;
}

export interface UseTopicPostsGridReturn {
  /** All ContentPost docs for this topic — raw subscription result. */
  allPosts: Post[];
  /** Master cells, keyed by aspectKey (NO_ASPECT_KEY for single-aspect topics). */
  mastersByAspect: Map<string, MasterCellModel>;
  /** Derivative cells: cells[aspectKey][platform] → cell model. */
  cells: Map<string, Map<PlatformId, DerivativeCellModel>>;
  loading: boolean;
  error: Error | null;
}

const aspectKeyFor = (post: Post): string =>
  (post.aspectKey ?? NO_ASPECT_KEY) as string;

const isStale = (post: Post): boolean => Boolean(post.staleSince);

const inferCellMode = (items: Post[]): CellMode => {
  if (items.length === 0) return 'single';
  // Explicit cellMode wins
  for (const it of items) {
    if (it.cellMode === 'variants' || it.cellMode === 'series' || it.cellMode === 'single') {
      return it.cellMode;
    }
  }
  // Heuristic fallbacks
  if (items.some((p) => p.seriesId)) return 'series';
  if (items.length > 1) return 'variants';
  return 'single';
};

/**
 * Subscribe to all ContentPost docs for a topic and group them into grid
 * cells. Loading state is true until the first snapshot resolves.
 */
export function useTopicPostsGrid(
  projectId: string | null | undefined,
  topicId: string | null | undefined,
  authorId?: string | null,
): UseTopicPostsGridReturn {
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(projectId && topicId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !topicId || authorId === 'all') {
      setAllPosts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const db = CoreFirebaseService.getInstance().firestore;
    const ref = collection(db, 'projects', projectId, 'content_posts');
    const q = authorId
      ? query(ref, where('authorId', '==', authorId), where('topicId', '==', topicId))
      : query(ref, where('topicId', '==', topicId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const posts = snap.docs.map(
          (d) => ({ ...(d.data() as Post), id: d.id }),
        );
        setAllPosts(posts);
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId, topicId, authorId]);

  const grouped = useMemo(() => {
    const mastersByAspect = new Map<string, MasterCellModel>();
    const cells = new Map<string, Map<PlatformId, DerivativeCellModel>>();

    // Skip archived posts so regenerated masters / deleted variants don't
    // pollute the grid display. The lifecycleStage='archived' marker is
    // stamped by the master regen flow (Section 5.1).
    const visible = allPosts.filter((p) => p.lifecycleStage !== 'archived');

    // ── Master cells ────────────────────────────────────────────
    for (const post of visible) {
      if (post.kind !== 'master') continue;
      const ak = aspectKeyFor(post);
      // Prefer the most-recent non-stale master if multiples exist.
      const existing = mastersByAspect.get(ak);
      if (!existing || (existing.master && isStale(existing.master) && !isStale(post))) {
        mastersByAspect.set(ak, {
          aspectKey: post.aspectKey ?? null,
          state: isStale(post) ? 'stale' : 'applied',
          master: post,
        });
      }
    }

    // ── Derivative cells (group by aspect × platform) ───────────
    for (const post of visible) {
      if (post.kind !== 'derivative') continue;
      // Canonical `platforms` array is the source of truth, but older
      // handler-side data may carry singular `platform` only. Fall back so
      // existing Firestore docs still render until regenerated.
      const platforms = post.platforms ?? [];
      const platform =
        (platforms[0] as PlatformId | undefined) ??
        ((post as any).platform as PlatformId | undefined);
      if (!platform) continue;
      const ak = aspectKeyFor(post);
      let aspectMap = cells.get(ak);
      if (!aspectMap) {
        aspectMap = new Map();
        cells.set(ak, aspectMap);
      }
      const cell = aspectMap.get(platform);
      if (!cell) {
        aspectMap.set(platform, {
          aspectKey: post.aspectKey ?? null,
          platform,
          cellMode: 'single',  // resolved later from collected items
          state: isStale(post) ? 'stale' : 'applied',
          items: [post],
          staleCount: isStale(post) ? 1 : 0,
        });
      } else {
        cell.items.push(post);
        if (isStale(post)) cell.staleCount += 1;
        if (isStale(post)) cell.state = 'stale';
      }
    }

    // ── Resolve cellMode + sort items per cell ──────────────────
    for (const aspectMap of cells.values()) {
      for (const cell of aspectMap.values()) {
        cell.cellMode = inferCellMode(cell.items);
        cell.items.sort((a, b) => {
          if (cell.cellMode === 'series') {
            const ai = a.seriesIndex ?? 0;
            const bi = b.seriesIndex ?? 0;
            return ai - bi;
          }
          if (cell.cellMode === 'variants') {
            // Primary first, then by createdAt asc
            if (a.isPrimaryVariant && !b.isPrimaryVariant) return -1;
            if (!a.isPrimaryVariant && b.isPrimaryVariant) return 1;
          }
          const at = (a.createdAt ?? '') as string;
          const bt = (b.createdAt ?? '') as string;
          return at.localeCompare(bt);
        });
      }
    }

    return { mastersByAspect, cells };
  }, [allPosts]);

  return { allPosts, ...grouped, loading, error };
}
